/*
 * WL shop grid: section (WL / MAINLINE / SPORTS) × product-type category.
 *
 * The collection template renders the current collection's product cards.
 * This script pulls the section's other source collections through the
 * Section Rendering API, merges everything into one product list, and then
 * filters / sorts / paginates on the client. Rail, chip and pagination links
 * are real links (progressive enhancement) that are intercepted for
 * client-side navigation with history.pushState.
 */
(() => {
  const root = document.querySelector('[data-wl-shop]');
  if (!root) return;

  const configEl = root.querySelector('[data-wl-shop-config]');
  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    console.error('[wl-shop] invalid config', error);
    return;
  }

  const grid = root.querySelector('[data-wl-grid]');
  const rail = root.querySelector('[data-wl-rail]');
  const chips = root.querySelector('[data-wl-chips]');
  const status = root.querySelector('[data-wl-status]');
  const empty = root.querySelector('[data-wl-empty]');
  const pagination = root.querySelector('[data-wl-pagination]');
  if (!grid) return;

  const categories = Array.isArray(config.categories) ? config.categories : [];
  const categoryKeys = new Set(categories.map((c) => c.key));
  const typeMap = new Map();
  categories.forEach((category) => {
    (category.types || []).forEach((type) => typeMap.set(normalizeType(type), category.key));
  });

  const sources = (config.sources || []).map((s) => String(s || '').trim()).filter(Boolean);
  const sourceSet = new Set(sources);
  const newHandle = String(config.newHandle || '').trim();
  const defaultCat = categoryKeys.has(config.defaultCat) ? config.defaultCat : 'new';
  const perPage = Math.max(1, Number(config.perPage) || 32);
  const newLimit = Math.max(0, Number(config.newLimit) || 0);
  const initialPath = location.pathname;
  const MAX_PAGES_PER_SOURCE = 8;
  const CACHE_PREFIX = `wl-shop:${config.sectionId}:`;
  const CACHE_TTL_MS = 10 * 60 * 1000;
  const CACHE_MAX_BYTES = 900 * 1024;

  // Title-based fallback when the product type is blank or not mapped.
  // Order matters: earlier rules win ("Trucker Hat" → hats, not jackets).
  const TITLE_RULES = [
    ['hats', /\b(hats?|caps?|beanies?|visors?|snapbacks?|bucket hat)\b/i],
    ['hoodies', /\b(hoodies?|sweatshirts?|crewnecks?|crew neck|zip[- ]?ups?|pullovers?|sweaters?|thermals?)\b/i],
    ['jackets', /\b(jackets?|flannels?|truckers?|coats?|parkas?|vests?|windbreakers?|anoraks?|overshirts?|puffers?|varsity)\b/i],
    ['bottoms', /\b(pants?|shorts?|sweatpants?|sweatshorts?|joggers?|denim|jeans?|trousers?|cargos?|bottoms?)\b/i],
    ['tees', /\b(t-?shirts?|tees?|tanks?|tank tops?|jerseys?|polos?|shirts?|long ?sleeves?|ringers?|tops?)\b/i],
    [
      'accessories',
      /\b(socks?|bags?|wallets?|masks?|gloves?|stickers?|toys?|shoes?|keychains?|pins?|towels?|blankets?|gift cards?|slides?|sandals?|belts?|scarf|scarves|lanyards?|patch(es)?|posters?|accessor(y|ies))\b/i,
    ],
  ];

  function normalizeType(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function categorize(product) {
    const mapped = typeMap.get(product.type);
    if (mapped) return mapped;
    for (const [key, pattern] of TITLE_RULES) {
      if (pattern.test(product.title)) return categoryKeys.has(key) ? key : null;
    }
    return null;
  }

  // ---- product store ----
  const products = new Map(); // handle -> product record
  const order = []; // insertion order (stable tiebreak)
  const sourceOrder = new Map(); // source handle -> [product handles in collection order]
  const loaded = new Set();
  const loading = new Map();

  function ingest(cards, source) {
    const list = sourceOrder.get(source) || [];
    sourceOrder.set(source, list);
    const sourceIndex = Math.max(0, sources.indexOf(source));
    cards.forEach((el) => {
      const handle = el.dataset.handle;
      if (!handle) return;
      let product = products.get(handle);
      if (!product) {
        const published = Number(el.dataset.published) || 0;
        product = {
          handle,
          el,
          type: normalizeType(el.dataset.type),
          title: el.dataset.title || '',
          published,
          // drops are published in batches; group by day so curated order survives within a drop
          publishedDay: Math.floor(published / 86400),
          available: el.dataset.available === 'true',
          sources: new Set(),
          rank: Number.POSITIVE_INFINITY,
        };
        product.cat = categorize(product);
        products.set(handle, product);
        order.push(product);
      }
      if (source && !product.sources.has(source)) {
        product.sources.add(source);
        list.push(handle);
        // deterministic tiebreak: earlier source, then collection position
        product.rank = Math.min(product.rank, sourceIndex * 100000 + list.length);
      }
      if (el.parentNode) el.parentNode.removeChild(el);
    });
  }

  // ---- fetching ----
  function readCache(key) {
    try {
      const raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      const entry = JSON.parse(raw);
      if (!entry || Date.now() - entry.t > CACHE_TTL_MS) {
        sessionStorage.removeItem(CACHE_PREFIX + key);
        return null;
      }
      return entry.html;
    } catch {
      return null;
    }
  }

  function writeCache(key, html) {
    if (!html || html.length > CACHE_MAX_BYTES) return;
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), html }));
    } catch {
      // quota exceeded or storage disabled – purge our keys and move on
      try {
        Object.keys(sessionStorage)
          .filter((k) => k.startsWith('wl-shop:'))
          .forEach((k) => sessionStorage.removeItem(k));
      } catch {
        /* ignore */
      }
    }
  }

  function sectionUrl(handle, page) {
    const url = new URL(`${config.collectionsUrl}/${handle}`, location.origin);
    url.searchParams.set('section_id', config.sectionId);
    if (page > 1) url.searchParams.set('page', String(page));
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.toString();
  }

  async function fetchSectionHtml(handle, page) {
    const key = `${handle}:${page}`;
    const cached = readCache(key);
    if (cached) return cached;
    const response = await fetch(sectionUrl(handle, page), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) throw new Error(`[wl-shop] ${handle} page ${page}: HTTP ${response.status}`);
    const html = await response.text();
    writeCache(key, html);
    return html;
  }

  function loadSource(handle) {
    if (loaded.has(handle)) return Promise.resolve();
    if (loading.has(handle)) return loading.get(handle);
    const task = (async () => {
      let page = 1;
      let pages = 1;
      try {
        do {
          const html = await fetchSectionHtml(handle, page);
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fetchedGrid = doc.querySelector('[data-wl-grid]');
          if (!fetchedGrid) break;
          pages = Math.max(1, Number(fetchedGrid.dataset.wlPages) || 1);
          ingest(Array.from(fetchedGrid.querySelectorAll('[data-wl-product]')), handle);
          page += 1;
        } while (page <= pages && page <= MAX_PAGES_PER_SOURCE);
      } catch (error) {
        console.warn(error);
      } finally {
        loaded.add(handle);
        loading.delete(handle);
      }
    })();
    loading.set(handle, task);
    return task;
  }

  // ---- state / urls ----
  function readState() {
    const params = new URLSearchParams(location.search);
    let cat = params.get('cat') || defaultCat;
    if (!categoryKeys.has(cat)) cat = defaultCat;
    let src = params.has('src')
      ? params.get('src') || ''
      : location.pathname === initialPath
        ? config.implicitSource || ''
        : '';
    if (src && !sourceSet.has(src)) src = '';
    let page = parseInt(params.get('page') || '1', 10);
    if (!(page > 0)) page = 1;
    return { cat, src, page };
  }

  function buildUrl(state) {
    const params = new URLSearchParams();
    if (state.cat && state.cat !== defaultCat) params.set('cat', state.cat);
    if (state.src) params.set('src', state.src);
    if (state.page > 1) params.set('page', String(state.page));
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) params.set('preview_theme_id', preview);
    const query = params.toString();
    return `${config.basePath}${query ? `?${query}` : ''}`;
  }

  function neededSources(state) {
    if (state.src) return [state.src];
    if (state.cat === 'new' && newHandle) return [newHandle];
    return sources.length ? sources : [config.currentHandle];
  }

  // ---- list computation ----
  function computeList(state) {
    let list;
    if (state.cat === 'new' && newHandle && !state.src) {
      list = (sourceOrder.get(newHandle) || []).map((h) => products.get(h)).filter(Boolean);
    } else {
      const showAll = state.cat === 'new' || state.cat === 'all';
      list = order.filter((p) => showAll || p.cat === state.cat);
      if (state.src) list = list.filter((p) => p.sources.has(state.src));
      const curatedSingle = (state.src && state.cat === 'new') || (state.cat === 'all' && sources.length === 1);
      if (curatedSingle) {
        // one collection, "new arrivals": keep the merchant's collection order
        const ordered = sourceOrder.get(state.src || sources[0]) || [];
        const rank = new Map(ordered.map((h, i) => [h, i]));
        list.sort((a, b) => (rank.get(a.handle) ?? 1e9) - (rank.get(b.handle) ?? 1e9));
      } else {
        list.sort((a, b) => b.publishedDay - a.publishedDay || a.rank - b.rank);
      }
      if (state.cat === 'new' && newLimit > 0) list = list.slice(0, newLimit);
    }
    return list;
  }

  // ---- painting ----
  function updateRail(state) {
    if (!rail) return;
    rail.querySelectorAll('[data-wl-cat]').forEach((link) => {
      const key = link.dataset.wlCat;
      link.href = buildUrl({ cat: key, src: state.src, page: 1 });
      if (key === state.cat) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  }

  function updateChips(state) {
    if (!chips) return;
    chips.querySelectorAll('[data-wl-src]').forEach((link) => {
      const key = link.dataset.wlSrc || '';
      link.href = buildUrl({ cat: state.cat, src: key, page: 1 });
      if (key === state.src) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  }

  function renderPagination(page, pages, state) {
    if (!pagination) return;
    if (pages <= 1) {
      pagination.hidden = true;
      pagination.replaceChildren();
      return;
    }
    const frag = document.createDocumentFragment();
    const span = (className, child) => {
      const el = document.createElement('span');
      el.className = className;
      if (typeof child === 'string') el.textContent = child;
      else if (child) el.appendChild(child);
      return el;
    };
    const link = (label, target) => {
      const a = document.createElement('a');
      a.href = buildUrl({ ...state, page: target });
      a.dataset.wlPage = String(target);
      a.textContent = label;
      return a;
    };
    if (page > 1) frag.appendChild(span('prev', link('‹ Previous', page - 1)));
    const windowStart = Math.max(1, page - 2);
    const windowEnd = Math.min(pages, page + 2);
    if (windowStart > 1) {
      frag.appendChild(span('page', link('1', 1)));
      if (windowStart > 2) frag.appendChild(span('deco', '…'));
    }
    for (let i = windowStart; i <= windowEnd; i += 1) {
      if (i === page) frag.appendChild(span('page current', String(i)));
      else frag.appendChild(span('page', link(String(i), i)));
    }
    if (windowEnd < pages) {
      if (windowEnd < pages - 1) frag.appendChild(span('deco', '…'));
      frag.appendChild(span('page', link(String(pages), pages)));
    }
    if (page < pages) frag.appendChild(span('next', link('Next ›', page + 1)));
    pagination.replaceChildren(frag);
    pagination.hidden = false;
  }

  function paint(state, partial) {
    const list = computeList(state);
    const pages = Math.max(1, Math.ceil(list.length / perPage));
    const page = Math.min(state.page, pages);
    const slice = list.slice((page - 1) * perPage, page * perPage);
    const frag = document.createDocumentFragment();
    slice.forEach((p) => frag.appendChild(p.el));
    grid.replaceChildren(frag);
    const isEmpty = list.length === 0 && !partial;
    if (empty) empty.hidden = !isEmpty;
    renderPagination(page, pages, state);
    root.classList.add('is-ready');
    return { list, page, pages };
  }

  let renderToken = 0;
  async function render({ scroll = false } = {}) {
    const token = ++renderToken;
    const state = readState();
    updateRail(state);
    updateChips(state);

    const missing = neededSources(state).filter((h) => !loaded.has(h));
    if (missing.length) {
      root.classList.add('is-loading');
      if (status) status.hidden = false;
      paint(state, true);
      await Promise.all(missing.map(loadSource));
      if (token !== renderToken) return;
      root.classList.remove('is-loading');
      if (status) status.hidden = true;
    }
    paint(state, false);
    if (scroll) scrollToTop();
  }

  function scrollToTop() {
    const target = root.getBoundingClientRect().top + window.scrollY - 8;
    if (window.scrollY > target) window.scrollTo({ top: Math.max(0, target), behavior: 'auto' });
  }

  // ---- navigation ----
  root.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-wl-cat], a[data-wl-src], [data-wl-pagination] a');
    if (!link || event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin) return;
    event.preventDefault();
    history.pushState({ wlShop: true }, '', url.pathname + url.search);
    render({ scroll: true });
  });

  window.addEventListener('popstate', () => {
    render({ scroll: false });
  });

  // ---- boot ----
  const ssrSource = grid.dataset.wlSource || config.currentHandle;
  ingest(Array.from(grid.querySelectorAll('[data-wl-product]')), ssrSource);
  const ssrPages = Math.max(1, Number(grid.dataset.wlPages) || 1);
  if (ssrPages <= 1) loaded.add(ssrSource);
  render({ scroll: false });
})();
